import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Flex,
  Box,
  Heading,
  Text,
  DataTable,
  LoadingOrbit,
  Card,
  CardHeader,
  CardContent,
  Button,
  Select,
  TagBadge,
  type SelectOption,
  badgeVariantToTagColor,
} from '@chatsdet/ui';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import type { TraceUploadListItem, Pagination } from '@chatsdet/types';
import { API_URL } from '../../../config';
import {
  formatDateTime,
  formatRelative,
  formatBytes,
  type JobStatus,
  statusVariants,
  statusLabels,
} from './useSuperadminData';

const TRACE_SCOPE_OPTIONS: SelectOption[] = [
  { id: 'all', label: 'All scopes' },
  { id: 'crawler', label: 'Crawler' },
  { id: 'exploratory', label: 'Exploratory' },
  { id: 'analysis', label: 'Analysis' },
];

const TRACE_SOURCE_OPTIONS: SelectOption[] = [
  { id: 'all', label: 'All sources' },
  { id: 'crawler', label: 'Crawler' },
  { id: 'exploratory', label: 'Exploratory' },
];

const TRACE_PAGE_SIZE_OPTIONS = [10, 20, 50] as const;

type TraceUploadResponse = {
  ok: true;
  data: TraceUploadListItem[];
  pagination: Pagination;
};

export function SuperadminTraceUploads() {
  const [tracePage, setTracePage] = React.useState(() => {
    const value = new URLSearchParams(window.location.search).get('page');
    const parsed = value ? parseInt(value, 10) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  });
  const [tracePageSize, setTracePageSize] = React.useState(() => {
    const value = new URLSearchParams(window.location.search).get('pageSize');
    const parsed = value ? parseInt(value, 10) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 20;
  });
  const [traceScopeFilter, setTraceScopeFilter] = React.useState<
    'all' | 'crawler' | 'exploratory' | 'analysis'
  >(() => {
    const value = new URLSearchParams(window.location.search).get('scope');
    if (value === 'crawler' || value === 'exploratory' || value === 'analysis') return value;
    return 'all';
  });
  const [traceSourceFilter, setTraceSourceFilter] = React.useState<
    'all' | 'crawler' | 'exploratory'
  >(() => {
    const value = new URLSearchParams(window.location.search).get('sourceType');
    if (value === 'crawler' || value === 'exploratory') return value;
    return 'all';
  });
  const [traceSearchInput, setTraceSearchInput] = React.useState(() => {
    return new URLSearchParams(window.location.search).get('search') ?? '';
  });
  const [traceSearch, setTraceSearch] = React.useState(() => {
    return new URLSearchParams(window.location.search).get('search') ?? '';
  });
  const [traceViewerError, setTraceViewerError] = React.useState<string | null>(null);
  const [openingTraceId, setOpeningTraceId] = React.useState<string | null>(null);

  const traceUploadsQuery = useQuery({
    queryKey: [
      'superadmin-trace-uploads',
      tracePage,
      tracePageSize,
      traceScopeFilter,
      traceSourceFilter,
      traceSearch,
    ],
    queryFn: async (): Promise<TraceUploadResponse> => {
      const params = new URLSearchParams();
      params.set('page', String(tracePage));
      params.set('pageSize', String(tracePageSize));
      if (traceScopeFilter !== 'all') params.set('scope', traceScopeFilter);
      if (traceSourceFilter !== 'all') params.set('sourceType', traceSourceFilter);
      if (traceSearch) params.set('search', traceSearch);
      const queryString = params.toString();
      const res = await fetch(
        `${API_URL}/superadmin/trace-uploads${queryString ? `?${queryString}` : ''}`,
        {
          credentials: 'include',
        }
      );
      if (!res.ok) {
        if (res.status === 403) {
          throw new Error('Access denied. Superadmin privileges required.');
        }
        const errorText = await res.text();
        throw new Error(errorText || 'Failed to load trace uploads');
      }
      const raw = (await res.json()) as TraceUploadResponse | { ok: false; error?: string };
      if (!raw.ok) {
        throw new Error(raw?.error ?? 'Failed to load trace uploads');
      }
      return raw;
    },
    placeholderData: (previous) => previous,
  });

  const applyTraceSearch = React.useCallback(() => {
    setTracePage(1);
    setTraceSearch(traceSearchInput.trim());
    setTraceViewerError(null);
  }, [traceSearchInput]);

  const clearTraceSearch = React.useCallback(() => {
    setTraceSearchInput('');
    setTraceSearch('');
    setTracePage(1);
    setTraceViewerError(null);
  }, []);

  const handleTraceScopeChange = React.useCallback((value: string) => {
    setTraceScopeFilter(value as 'all' | 'crawler' | 'exploratory' | 'analysis');
    setTracePage(1);
    setTraceViewerError(null);
  }, []);

  const handleTraceSourceChange = React.useCallback((value: string) => {
    setTraceSourceFilter(value as 'all' | 'crawler' | 'exploratory');
    setTracePage(1);
    setTraceViewerError(null);
  }, []);

  const handleTracePageSizeChange = React.useCallback((size: number) => {
    if (!Number.isNaN(size) && size > 0) {
      setTracePageSize(size);
      setTracePage(1);
      setTraceViewerError(null);
    }
  }, []);

  const handleTracePageChange = React.useCallback((nextPage: number) => {
    setTraceViewerError(null);
    setTracePage(nextPage);
  }, []);

  const handleTraceSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      applyTraceSearch();
    }
  };

  const handleOpenTrace = React.useCallback(async (uploadId: string) => {
    setTraceViewerError(null);
    setOpeningTraceId(uploadId);
    try {
      const res = await fetch(`${API_URL}/superadmin/trace-uploads/${uploadId}/viewer`, {
        credentials: 'include',
      });
      const payload = (await res.json().catch(() => null)) as {
        ok: boolean;
        viewerUrl?: string;
        error?: string;
      } | null;
      if (!res.ok || !payload || payload.ok !== true || !payload.viewerUrl) {
        const message =
          payload?.error ??
          (res.status === 403
            ? 'Access denied. Superadmin privileges required.'
            : 'Failed to open trace viewer');
        throw new Error(message);
      }
      window.open(payload.viewerUrl, '_blank', 'noopener');
    } catch (error) {
      console.error('Failed to open trace viewer', error);
      setTraceViewerError(error instanceof Error ? error.message : 'Failed to open trace viewer');
    } finally {
      setOpeningTraceId(null);
    }
  }, []);

  const traceColumns = React.useMemo<ColumnDef<StockFeatures, TraceUploadListItem>[]>(
    () => [
      {
        header: 'Org',
        cell: ({ row }) => {
          const org = row.original.org;
          if (!org) {
            return <Text className="text-muted-foreground">—</Text>;
          }
          return (
            <Box>
              <Text weight="medium">{org.name ?? org.id}</Text>
              {org.name && (
                <Text size="xs" className="text-muted-foreground">
                  {org.id}
                </Text>
              )}
            </Box>
          );
        },
      },
      {
        header: 'Property',
        cell: ({ row }) => {
          const property = row.original.property;
          if (!property) {
            return <Text className="text-muted-foreground">—</Text>;
          }
          return (
            <Box>
              <Text>{property.name ?? property.id}</Text>
              {property.name && (
                <Text size="xs" className="text-muted-foreground">
                  {property.id}
                </Text>
              )}
            </Box>
          );
        },
      },
      {
        header: 'Scope',
        cell: ({ row }) => (
          <Box className="space-y-1">
            <TagBadge name={row.original.scope} color="gray" hideDot />
            <Text size="xs" className="text-muted-foreground">
              {row.original.sourceType ?? '—'}
            </Text>
          </Box>
        ),
      },
      {
        header: 'Source',
        cell: ({ row }) => {
          const label = row.original.sourceLabel ?? row.original.sourceType ?? '—';
          const sourceId = row.original.sourceId;
          const hasResourcesFlag = row.original.metadata?.hasResources;
          return (
            <Box className="space-y-1">
              <Text weight="medium">{label}</Text>
              {sourceId && (
                <Text size="xs" className="text-muted-foreground">
                  ID: {sourceId}
                </Text>
              )}
              <Text size="xs" className="text-muted-foreground font-mono break-all">
                {row.original.traceKey}
              </Text>
              {hasResourcesFlag === false && (
                <TagBadge name="Resources missing" color="red" hideDot />
              )}
            </Box>
          );
        },
      },
      {
        header: 'Origin URL',
        cell: ({ row }) => {
          const originUrl = row.original.originUrl;
          if (!originUrl) {
            return <Text className="text-muted-foreground">—</Text>;
          }
          return (
            <a
              href={originUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline text-sm font-mono break-all"
            >
              {originUrl}
            </a>
          );
        },
      },
      {
        header: 'Captured',
        cell: ({ row }) => (
          <Box className="space-y-1">
            <Text>{formatDateTime(row.original.capturedAt ?? row.original.uploadedAt)}</Text>
            <Text size="xs" className="text-muted-foreground">
              Uploaded {formatRelative(row.original.uploadedAt)}
            </Text>
          </Box>
        ),
      },
      {
        header: 'Size',
        cell: ({ row }) => <Text>{formatBytes(row.original.archiveSizeBytes)}</Text>,
      },
      {
        header: 'Job Status',
        cell: ({ row }) => {
          const job = row.original.ingestionJob;
          if (!job) {
            return <Text className="text-muted-foreground">Pending</Text>;
          }
          const status = job.status as JobStatus;
          return (
            <Box className="space-y-1">
              <TagBadge
                name={statusLabels[status] ?? status}
                color={badgeVariantToTagColor(statusVariants[status] ?? 'secondary')}
                hideDot
              />
              <Text size="xs" className="text-muted-foreground">
                Updated {formatRelative(job.updatedAt)}
              </Text>
            </Box>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <Button
            variant="outline"
            onPress={() => handleOpenTrace(row.original.id)}
            isDisabled={openingTraceId === row.original.id}
          >
            {openingTraceId === row.original.id ? 'Opening…' : 'View Trace'}
          </Button>
        ),
      },
    ],
    [handleOpenTrace, openingTraceId]
  );

  const traceUploads = traceUploadsQuery.data?.data ?? [];
  const tracePagination = traceUploadsQuery.data?.pagination;
  const traceTotalPages = tracePagination?.totalPages ?? 0;
  const traceCurrentPage = tracePagination?.page ?? tracePage;
  const traceSearchDirty = traceSearchInput.trim() !== traceSearch;
  const traceUploadsErrorMessage =
    traceUploadsQuery.error instanceof Error
      ? traceUploadsQuery.error.message
      : 'Failed to load trace uploads';
  const traceTotalItems = tracePagination?.totalItems ?? traceUploads.length;
  const tracePageSizeForTable = tracePagination?.pageSize ?? tracePageSize;

  return (
    <Box className="space-y-6">
      <Card padding="lg">
        <CardHeader>
          <Flex align="center" justify="between" wrap gap="md">
            <div>
              <Heading level={3}>Trace Uploads</Heading>
              <Text size="sm" className="text-muted-foreground mt-1">
                Inspect captured Playwright traces from crawler, exploratory, and analysis runs.
              </Text>
            </div>
            {traceUploadsQuery.isFetching && <LoadingOrbit size="sm" />}
          </Flex>
        </CardHeader>
        <CardContent>
          <Flex gap="md" wrap className="mb-4">
            <Select
              label="Scope"
              items={TRACE_SCOPE_OPTIONS}
              selectedKey={traceScopeFilter}
              onSelectionChange={(key) => handleTraceScopeChange(String(key))}
              className="min-w-[160px]"
            />
            <Select
              label="Source type"
              items={TRACE_SOURCE_OPTIONS}
              selectedKey={traceSourceFilter}
              onSelectionChange={(key) => handleTraceSourceChange(String(key))}
              className="min-w-[160px]"
            />
            <Box className="min-w-[240px]">
              <Text size="xs" className="text-muted-foreground mb-1 block">
                Search (URL, source, trace key)
              </Text>
              <input
                value={traceSearchInput}
                onChange={(event) => setTraceSearchInput(event.target.value)}
                onKeyDown={handleTraceSearchKeyDown}
                className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
                placeholder="example.com/api"
              />
            </Box>
            <Flex gap="sm" align="end">
              <Button onPress={applyTraceSearch} isDisabled={!traceSearchDirty}>
                Search
              </Button>
              <Button
                variant="ghost"
                onPress={clearTraceSearch}
                isDisabled={!traceSearch && !traceSearchInput}
              >
                Clear
              </Button>
            </Flex>
          </Flex>

          {traceViewerError && (
            <Box className="mb-4 border border-destructive/40 bg-destructive/5 rounded-md px-3 py-2">
              <Text size="sm" className="text-destructive">
                {traceViewerError}
              </Text>
            </Box>
          )}

          {traceUploadsQuery.isLoading ? (
            <Flex align="center" justify="center" className="py-8">
              <LoadingOrbit size="lg" />
            </Flex>
          ) : traceUploadsQuery.isError ? (
            <Text className="text-destructive">{traceUploadsErrorMessage}</Text>
          ) : traceUploads.length ? (
            <Box className="space-y-4">
              <DataTable
                columns={traceColumns}
                data={traceUploads}
                serverPagination={{
                  page: traceCurrentPage,
                  pageSize: tracePageSizeForTable,
                  pageCount: traceTotalPages,
                  totalItems: traceTotalItems,
                  pageSizeOptions: Array.from(TRACE_PAGE_SIZE_OPTIONS),
                  onPageChange: handleTracePageChange,
                  onPageSizeChange: handleTracePageSizeChange,
                  isLoading: traceUploadsQuery.isFetching,
                }}
              />
            </Box>
          ) : (
            <Text className="text-muted-foreground">
              No trace uploads found with the current filters.
            </Text>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
